/**
 * SubscriptionContext — v2 aware.
 *
 * Reads both:
 *   - Legacy `/api/subscription/*` (BASIC/STANDARD/PREMIUM + module access map).
 *   - New `/api/subscriptions/my` (Clinic/Chain tier + trial/grace/expired
 *     lifecycle + premium_features + write_allowed + upgrade_required_message).
 *
 * Both surfaces are exposed on the same context so downstream components can
 * keep using `.tier`, `.access`, `.trialActive` (legacy) while new components
 * can read `.v2.status`, `.v2.tier`, `.v2.write_allowed`, `.v2.premium_features`.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_V2 = {
  tenant_id: null,
  subscription_id: null,
  tier: null,
  status: null,           // pending_selection | trialing | active | grace | expired | cancelled
  clinic_limit: 0,
  premium_features: {},
  write_allowed: true,
  in_trial: false,
  in_grace: false,
  days_left: null,
  current_period_end: null,
  grace_ends_at: null,
  downgrade_scheduled: null,
  upgrade_required_message: null,
  beta_migrated: false,
};

const SubscriptionContext = createContext({
  tier: 'BASIC',
  access: {},
  superAdminBypass: false,
  trialActive: false,
  trialDaysLeft: null,
  isBranch: false,
  inheritedFromHead: false,
  headClinicId: null,
  headClinicName: null,
  loading: true,
  v2: DEFAULT_V2,
  refresh: () => {},
});

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    tier: 'BASIC', access: {}, superAdminBypass: false,
    trialActive: false, trialDaysLeft: null,
    isBranch: false, inheritedFromHead: false,
    headClinicId: null, headClinicName: null,
    v2: DEFAULT_V2,
    loading: true,
  });

  const refresh = useCallback(async () => {
    if (!user) { setState((s) => ({ ...s, loading: false })); return; }
    try {
      const [a, m, v2] = await Promise.all([
        axios.get(`${API}/subscription/access`).catch(() => ({ data: {} })),
        axios.get(`${API}/subscription/my`).catch(() => ({ data: {} })),
        axios.get(`${API}/subscriptions/my`).catch(() => ({ data: DEFAULT_V2 })),
      ]);
      setState({
        tier: a.data.tier || 'BASIC',
        access: a.data.access || {},
        superAdminBypass: !!a.data.super_admin_bypass,
        trialActive: !!m.data.trial_active,
        trialDaysLeft: m.data.trial_days_left,
        isBranch: !!m.data.is_branch,
        inheritedFromHead: !!m.data.inherited_from_head,
        headClinicId: m.data.head_clinic_id || null,
        headClinicName: m.data.head_clinic_name || null,
        v2: { ...DEFAULT_V2, ...(v2?.data || {}) },
        loading: false,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SubscriptionContext.Provider value={{ ...state, refresh }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}

/** Gate a component behind a LEGACY module — unchanged. */
export function ModuleGate({ module, children }) {
  const { access, tier, superAdminBypass, loading } = useSubscription();
  if (loading) return <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>;
  if (superAdminBypass || access[module]) return children;
  return (
    <div className="p-12 max-w-xl mx-auto text-center" data-testid={`gate-${module}`}>
      <div className="text-6xl mb-4">🔒</div>
      <h1 className="text-2xl font-bold text-slate-800 mb-2">This module is locked</h1>
      <p className="text-slate-500 mb-6">
        The <b className="capitalize">{module.replace('-', ' ')}</b> module isn&apos;t included in
        your current <b>{tier}</b> plan. Upgrade to Premium to unlock service &amp; repair,
        full analytics, trade-ins, and multi-branch.
      </p>
      <a href="/#pricing" target="_blank" rel="noreferrer"
         className="inline-block px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded"
         data-testid={`gate-${module}-upgrade-cta`}>
        See Plans
      </a>
    </div>
  );
}
